import 'core-js'
import 'whatwg-fetch'

import { h, render, Component } from 'preact'
import { Suspense, lazy } from 'preact/compat'

import { Client as TwilioClient } from '@twilio/conversations'

import Utils from './webchat/utils'
import { SVG, Bubble, Form } from './webchat/components'

const Picker = lazy(() => import('emoji-mart/dist/components/picker/picker'))

class Webchat extends Component {
  constructor(props) {
    super()

    this.config = props.config
    this.options = props.options

    this.state = {
      opened: this.options.opened,
      internet: navigator.onLine,
      emptyTextarea: true,
      emoji: false,
    }

    this.resetCache()

    this.getStatus = this.getStatus.bind(this)
    this.focusChat = this.focusChat.bind(this)
    this.scrollToBottom = this.scrollToBottom.bind(this)
    this.handleConnectionChange = this.handleConnectionChange.bind(this)
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.handleDragEnter = this.handleDragEnter.bind(this)
    this.handleDragLeave = this.handleDragLeave.bind(this)

    window.addEventListener('online', this.handleConnectionChange)
    window.addEventListener('offline', this.handleConnectionChange)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    this.init()
  }

  componentDidUpdate(prevProps, prevState) {
    const { chat } = this.config

    if (
      this.state.configReady &&
      (this.state.online ||
        (!chat.hideWhenOffline && this.state.online != undefined))
    ) {
      this.handleChatReady()
    }

    if (!prevState.canCompose && this.state.canCompose) {
      this.resizeInput()
    }
  }

  init() {
    const { user } = this.config
    this.loaded = false

    let store = this.getStore()
    if (user.digest && store.identity) {
      store = this.resetStore()
    }

    if (
      store.identity ||
      (user.digest && (store.lastMessageSeenAt || this.state.opened))
    ) {
      this.connect()
    } else {
      this.getStatus()
    }

    // Update status every 5 minutes
    clearInterval(this.statusInterval)
    this.statusInterval = setInterval(this.getStatus, 5 * 60 * 1000)
  }

  postMessage(event, params) {
    params || (params = {})
    params.event = event

    window.parent.postMessage(params, '*')
  }

  handleCallback(callback, data) {
    this.postMessage('missive-webchat-callback', {
      data: { callback, data },
    })
  }

  resetCache() {
    this.cache = {
      entries: [],
    }

    this.cache.entries._byId = {}
  }

  reset({ user } = {}) {
    this.config.user = user

    this.disconnect(
      () => {
        this.resetStore()
        this.init()
      },
      { reset: true },
    )
  }

  handleChatReady() {
    if (this.loaded) return
    this.loaded = true

    const { messages } = this.config
    const { identity, opened } = this.getStore()

    this.addEntry({
      timestamp: -1,
      type: 'bubble',
      author: 'bot',
      body: messages.welcome,
    })

    if (!identity) {
      this.identify()
    }

    this.postMessage('missive-webchat-loaded')

    if (opened && this.config.chat.rememberOpenState) {
      setTimeout(() => {
        this.open()
      }, 225)
    }
  }

  updateCSSVars() {
    const { chat } = this.config

    const { documentElement } = document
    for (let key in chat.variables) {
      documentElement.style.setProperty(`--${key}`, chat.variables[key])
    }
  }

  getMeta() {
    return new Promise((resolve, reject) => {
      this.messageId || (this.messageId = 0)
      this.messageId++

      const messageId = `w${this.messageId}`

      this.promises || (this.promises = {})
      this.promises[messageId] = { resolve, reject }

      this.postMessage('missive-webchat-meta', { messageId })
    })
  }

  getStore() {
    if (this.store) return this.store

    const { id } = this.config
    const store = (() => {
      try {
        if (localStorage[`missive.${id}`]) {
          return JSON.parse(localStorage[`missive.${id}`])
        }
      } catch (e) {}

      return { id }
    })()

    this.store = store
    return store
  }

  resetStore() {
    const { id } = this.config

    delete this.store
    try {
      delete localStorage[`missive.${id}`]
      sessionStorage.clear()
    } catch (e) {}

    return this.getStore()
  }

  setInStore(hash) {
    const { id } = this.config
    const store = this.getStore()

    for (var property in hash) {
      store[property] = hash[property]
    }

    try {
      localStorage[`missive.${id}`] = JSON.stringify(store)
    } catch (e) {}
  }

  handleUpdate({ config, options }) {
    if (config) {
      this.config = config

      this.updateCSSVars()

      if (!this.state.configReady) {
        this.setState({ configReady: true })
      }
    }

    if (options) {
      this.options = options
    }
  }

  handleConnectionChange() {
    clearTimeout(this.internetTimeout)
    this.internetTimeout = setTimeout(() => {
      const hadInternet = this.state.internet
      this.setState({ internet: navigator.onLine }, () => {
        const hasInternet = this.state.internet

        if (!hadInternet && hasInternet) {
          this.reconnect()
        }
      })
    }, 100)
  }

  handleConnectionError(e) {
    const { messages } = this.config

    setTimeout(() => {
      console.error(e)
      this.setState({ error: true })

      this.addEntry({
        type: 'bubble',
        author: 'bot',
        body: messages.error,
      })
    })
  }

  handleVisibilityChange() {
    this.markVisibleEntriesAsRead()
  }

  get(...args) {
    return this.request('GET', ...args)
  }

  post(...args) {
    return this.request('POST', ...args)
  }

  request(verb, path, args = {}) {
    return new Promise(async (resolve, reject) => {
      const { id } = this.config

      args.method = verb
      args.credentials = 'omit'

      if (verb != 'GET') {
        args.headers = { 'Content-Type': 'application/json' }
        args.body = JSON.stringify(args.body || {})
      }

      let response

      try {
        response = await fetch(
          `${process.env.TWILIO_CHAT_ENDPOINT}/${id}${path}?domain=${this.options.domain}`,
          args,
        )
      } catch (e) {
        return reject(e)
      }

      if (response.status == 401) {
        clearInterval(this.statusInterval)
        return this.postMessage('missive-webchat-destroy')
      }

      if (!response.ok) {
        const errorMsg = await response.text()
        return reject(new Error(errorMsg))
      }

      const json = await response.json()
      this.cache.username = json.username
      this.cache.avatarUrl = json.avatar_url
      this.cache.canHideBranding = json.can_hide_branding

      this.postMessage('missive-webchat-config', { data: json.config })

      resolve(json)
    })
  }

  async getStatus() {
    try {
      const json = await this.get('/status')
      this.connecting = false
      this.setState({ online: json.online })
    } catch (e) {
      this.handleConnectionError(e)
    }
  }

  async connect() {
    // Make sure we don't connect twice
    if (this.connecting || this.client) return
    this.connecting = true

    const prevConversation = this.conversation
    if (prevConversation) this.forceUpdate()

    const { user } = this.config
    const store = this.getStore()

    const body = {
      identity: user.digest || store.identity || null,
    }

    // TODO remove store.channel, 3-6 months after deploying
    // switch from TwilioChat to TwilioConversations
    const conversation = store.conversation || store.channel

    if (conversation) {
      body.conversation = conversation
    }

    try {
      const json = await this.post('/connect', { body })
      this.setState({ online: json.online })

      this.client = new TwilioClient(json.token)
      this.conversation = await this.client.getConversationByUniqueName(
        json.conversation,
      )
      this.conversation.on('messageAdded', (message) => {
        this.handleConversationMessage(message)
        this.resetReconnection()
      })

      this.resetReconnection()

      if (user && !user.digest) {
        this.setInStore({
          identity: json.identity,
          conversation: json.conversation,
        })
      }
    } catch (e) {
      return this.handleConnectionError(e)
    }

    this.connecting = false

    const history = await this.conversation.getMessages()
    this.isNewChat = history && !history.items.length
    this.handleMessagesHistory(history)

    this.setState({ canCompose: true }, this.focusChat)
  }

  resetReconnection() {
    const delay = 30 * 60 * 1000 // 30 minutes

    clearTimeout(this.reconnectTimeout)
    this.reconnectTimeout = setTimeout(() => {
      this.reconnect()
    }, delay)
  }

  reconnect() {
    if (!this.client) return
    this.disconnect(this.connect)
  }

  async disconnect(callback, { reset } = {}) {
    try {
      await this.client.shutdown()
    } catch (e) {}

    this.client = null
    const state = {}

    if (reset) {
      this.resetCache()
      state.online = undefined
    }

    this.connecting = false
    this.setState(state, callback)
  }

  async identify() {
    const { user, visitor, messages } = this.config
    const store = this.getStore()

    const fields = []

    let name = 'name' in user ? user.name : store.name
    let email = 'email' in user ? user.email : store.email

    if (visitor.askName && !name && !email) {
      fields.push({
        type: 'text',
        name: 'name',
        placeholder: messages.identifyName,
        required: visitor.requireName,
        validate: (value) => {
          return value.length <= 250
        },
      })
    }

    if (visitor.askEmail && !email) {
      fields.push({
        type: 'email',
        name: 'email',
        placeholder: messages.identifyEmail,
        required: visitor.requireEmail,
        validate: (value) => {
          if (value.length > 250) {
            return false
          }
          return Utils.EMAIL_REGEX.test(String(value).toLowerCase())
        },
      })
    }

    if (visitor.requireConsent && !name && !email) {
      fields.push({
        type: 'checkbox',
        name: 'consent',
        label: messages.identifyConsent,
        required: true,
      })
    }

    if (!fields.length) {
      this.setState({ canCompose: true }, this.focusChat)
      return
    }

    this.addEntry({
      type: 'bubble',
      author: 'bot',
      body: messages.identify,
    })

    this.addEntry({
      type: 'form',
      fields: fields,
      onSubmit: (f) => {
        name || (name = f.name)
        email || (email = f.email)

        this.addEntry({
          type: 'bubble',
          author: 'bot',
          body: (name || email
            ? [`${messages.identifyThanks}${name ? ` ${name}` : ''}!`]
            : []
          ).concat(messages.help),
        })

        if (f.name) this.setInStore({ name })
        if (f.email) this.setInStore({ email })

        this.setState({ canCompose: true }, this.focusChat)
        this.handleCallback('onVisitorIdentified', { name, email })
      },
    })
  }

  resetBadgeCount() {
    this.badgeCount = 0
    this.updateBadgeCount()
  }

  incrementBadgeCount() {
    this.badgeCount || (this.badgeCount = 0)
    this.badgeCount++

    this.updateBadgeCount()
  }

  updateBadgeCount() {
    clearTimeout(this.badgeCountTimer)
    this.badgeCountTimer = setTimeout(() => {
      this.postMessage('missive-webchat-badge', { data: this.badgeCount })
    }, 0)
  }

  markVisibleEntriesAsRead() {
    if (this.state.opened && document.visibilityState == 'visible') {
      this.markEntriesAsRead()
    }
  }

  markEntriesAsRead() {
    const { entries } = this.cache
    if (!this.client || !entries.length) return

    clearTimeout(this.markAsReadTimeout)
    this.markAsReadTimeout = setTimeout(() => {
      const lastEntry = entries[entries.length - 1]
      const { lastMessageSeenAt } = this.getStore()
      const { timestamp } = lastEntry

      if (!lastMessageSeenAt || timestamp > lastMessageSeenAt) {
        this.setInStore({ lastMessageSeenAt: timestamp })
      }

      if (this.badgeCount) {
        this.resetBadgeCount()
        this.sendTwilioEvent('read')
      }
    })
  }

  async handleMessagesHistory(history) {
    if (!history || !history.items.length) return

    for (let message of history.items) {
      this.handleConversationMessage(message, { silent: true })
    }

    if (history.hasPrevPage && this.cache.entries.length < 100) {
      const prevHistory = await history.prevPage()
      this.handleMessagesHistory(prevHistory)
    }
  }

  handleConversationMessage(message, { silent } = {}) {
    if (message.attributes.event) return

    if (this.cache.entries._byId[message.sid]) return
    this.cache.entries._byId[message.sid] = message

    const store = this.getStore()
    const { lastMessageSeenAt } = store
    const { chat, user } = this.config

    const identity = user.digest || store.identity
    const author = message.author == identity ? 'user' : 'app'
    const timestamp = Math.floor(new Date(message.dateCreated)) / 1000

    const avatar = chat.showAvatars
      ? author == 'app'
        ? chat.avatarUrl || this.cache.avatarUrl
        : user.avatarUrl
      : null

    const username = chat.showNames
      ? author == 'app'
        ? chat.name || this.cache.username
        : author == 'user'
        ? 'You'
        : null
      : null

    this.addEntry({
      type: 'bubble',
      author,
      username,
      avatar,
      timestamp,
      body: message.state.body,
      media: message.media,
      message: message,
    })

    if (!lastMessageSeenAt || timestamp > lastMessageSeenAt) {
      if (author == 'app') {
        this.incrementBadgeCount()
      }

      this.markVisibleEntriesAsRead()

      if (this.options.hidden) {
        this.open({ doNotFocus: true })
      }
    }

    if (author == 'app' && !silent) {
      this.handleCallback('onNewMessage', message.state.body)
    }
  }

  open({ doNotFocus } = {}) {
    const { user } = this.config

    if (user.digest) {
      this.connect()
    }

    if (this.badgeCount) {
      this.markEntriesAsRead()
    }

    this.postMessage('missive-webchat-open')
    this.setState({ opened: true }, () => {
      if (doNotFocus) return
      setTimeout(this.focusChat, this.options.transitionDuration)
    })

    this.setInStore({ opened: true })
  }

  close() {
    this.setState({ opened: false, emoji: false })
    this.postMessage('missive-webchat-close')

    this.setInStore({ opened: false })
  }

  toggleEmoji() {
    this.setState({ emoji: !this.state.emoji })
  }

  setEmptyTextarea() {
    const value = this.textarea.value.trim()

    if (value.length && this.state.emptyTextarea) {
      this.setState({ emptyTextarea: false })
    } else if (!value.length && !this.state.emptyTextarea) {
      this.setState({ emptyTextarea: true })
    }
  }

  handleInput() {
    this.setEmptyTextarea()
    this.resizeInput()
  }

  addCharacter(char, { moveToCursor } = {}) {
    const carret = this.textarea.selectionStart

    const oldValue = this.textarea.value
    const newValue =
      oldValue.substring(0, carret) + char + oldValue.substring(carret)

    this.textarea.value = newValue
    this.textarea.selectionStart = this.textarea.selectionEnd =
      carret + char.length

    this.resizeInput()

    if (moveToCursor) {
      this.textarea.blur()
      this.focusChat()
    }
  }

  resizeInput() {
    if (!this.textarea) return

    this.shadowTextarea.value = this.textarea.value
    const height = this.shadowTextarea.scrollHeight
    this.textarea.style.height = `${height}px`

    if (height > this.inputHeight || 0) {
      this.scrollToBottom()
    }

    this.inputHeight = height
  }

  handleEmoji(emoji, e) {
    this.addCharacter(emoji.native)
    this.setState({ emoji: false })

    this.focusChat()
  }

  focusChat() {
    if (Utils.isMobile() || !this.textarea || !this.state.opened) return
    this.textarea.focus()
  }

  scrollToBottom() {
    if (!this.main) return
    this.main.scrollTop = this.main.scrollHeight
  }

  handleFocus() {
    this.setState({ emoji: false })
  }

  submitMessage(e) {
    e.preventDefault()

    const value = this.textarea.value.trim()
    if (!value.length) return

    this.textarea.value = ''
    this.setState({ emptyTextarea: true })
    this.resizeInput()

    this.sendTwilioMessage({ body: value })
  }

  sendTwilioEvent(type, data) {
    const event = { type }
    if (data) event.data = data

    this.sendTwilioMessage({ body: '', event })
  }

  async sendTwilioMessage({ body, event }, retries = 0) {
    await this.connect()

    const store = this.getStore()
    const { user, messages } = this.config

    const name = user.name || store.name
    const email = user.email || store.email

    const data = { name, email }
    if (event) data.event = event
    if (user.digest) data.digest = user.digest
    if (user.avatarUrl) data.avatarUrl = user.avatarUrl

    const meta = await this.getMeta()
    if (meta) data.meta = meta

    try {
      await this.conversation.sendMessage(body, data)

      if (this.isNewChat) {
        this.isNewChat = false
        this.handleCallback('onChatStarted', { name, email })
      }
    } catch (e) {
      if (retries >= 1) {
        this.addEntry({
          type: 'bubble',
          author: 'bot',
          body: messages.error,
        })
      } else {
        this.disconnect(() => {
          this.sendTwilioMessage({ body, event }, retries + 1)
        })
      }
    }
  }

  addEntry(entry) {
    if (entry.type == 'bubble' && Array.isArray(entry.body)) {
      for (let msg of entry.body) {
        let e = JSON.parse(JSON.stringify(entry))
        e.body = msg

        this.addEntry(e)
      }

      return
    }

    entry.timestamp || (entry.timestamp = Utils.now())

    this.cache.entries.push(entry)

    clearTimeout(this.entriesTimeout)
    this.entriesTimeout = setTimeout(() => {
      this.cache.entries.sort((a, b) => {
        return a.timestamp - b.timestamp
      })

      this.forceUpdate(this.scrollToBottom)
    })
  }

  handleKeyDown(e) {
    switch (e.keyCode) {
      case 13:
        e.preventDefault()
        e.stopImmediatePropagation()

        if (e.altKey || e.shiftKey) {
          return this.addCharacter('\r', { moveToCursor: true })
        }

        this.submitMessage(e)
    }
  }

  handleFile(e) {
    for (let file of e.target.files) {
      const formData = new FormData()
      formData.append('file', file)
      this.sendTwilioMessage({ body: formData })
    }

    e.target.value = null
    this.setState({ dragging: false })
  }

  handleDragEnter(e) {
    e.stopPropagation()
    this.setState({ dragging: true })

    setTimeout(() => {
      clearTimeout(this.dragTimeout)
    })
  }

  handleDragLeave(e) {
    e.stopPropagation()

    clearTimeout(this.dragTimeout)
    this.dragTimeout = setTimeout(() => {
      this.setState({ dragging: false })
    }, 10)
  }

  renderEntries() {
    const { entries } = this.cache

    let groups = []
    let currentGroup = null
    let lastTime = null

    let i = -1
    for (let entry of entries) {
      i = i + 1
      let nextEntry = entries[i + 1]

      if (
        entry.author &&
        entry.author != 'bot' &&
        (!lastTime || entry.timestamp - lastTime > 3 * 60)
      ) {
        lastTime = entry.timestamp
        currentGroup = [
          <div class="date">{Utils.formatTimestamp(entry.timestamp)}</div>,
        ]
        groups.push(currentGroup)
      }

      let entryComponent = (() => {
        switch (entry.type) {
          case 'bubble':
            return (
              <Bubble
                key={entry.message && entry.message.sid}
                data={{
                  'data-author': entry.author,
                  'data-next-author': nextEntry && nextEntry.author,
                }}
                body={entry.body}
                media={entry.media}
                avatar={entry.avatar}
                author={entry.author}
                username={entry.username}
                onMediaLoaded={this.scrollToBottom}
              />
            )
          case 'form':
            return <Form config={this.config} {...entry} />
        }
      })()

      if (entryComponent) {
        if (!currentGroup) {
          currentGroup = []
          groups.push(currentGroup)
        }

        currentGroup.push(entryComponent)
      }
    }

    return (
      <div class="row">
        {groups.map((group) => {
          return <div class="row">{group}</div>
        })}
      </div>
    )
  }

  renderLoading() {
    if (!this.connecting || this.state.error) return null

    return (
      <div class="row spacer">
        <div class="icon-loading">
          <SVG name="circle" size={32} />
        </div>
      </div>
    )
  }

  render(props, state) {
    const { messages, chat } = this.config
    const isMobile = Utils.isMobile()
    const status = chat.showOnlineStatus
      ? state.online == undefined
        ? 'connecting'
        : state.online
        ? 'online'
        : 'offline'
      : null

    const containerProps = {
      'data-state': state.opened ? 'opened' : 'closed',
    }

    if (state.canCompose && state.online) {
      containerProps.onDragEnter = this.handleDragEnter
      containerProps.onDragLeave = this.handleDragLeave
    }

    return (
      <div id="webchat" {...containerProps}>
        <div id="header">
          <div id="status">
            <div>
              <h1>{messages.title}</h1>
              {status && (
                <p data-status={status}>
                  <span class="icon-status icon-status-online"></span>
                  <span class="icon-status icon-status-offline"></span>
                  <span class="icon-status icon-status-connecting"></span>
                  {messages[status]}
                </p>
              )}
            </div>
          </div>
          <span id="close">
            <span
              onClick={(e) => {
                this.close()
                e.stopPropagation()
              }}
            >
              <SVG name="close" size={36} />
            </span>
          </span>
          <span id="logo">
            <SVG name="logo" size={30} />
          </span>
        </div>

        <div id="main" ref={(main) => (this.main = main)}>
          <div class="spacer">
            {!this.cache.entries.length ? this.renderLoading() : null}
          </div>

          <div class="entries">
            {this.renderEntries()}
            {this.cache.entries.length ? this.renderLoading() : null}

            {this.cache.canHideBranding && chat.hideBranding ? null : (
              <div class="row">
                <a
                  id="missive-link"
                  tabindex="-1"
                  href={`https://missiveapp.com/?utm_source=${this.options.domain}&utm_medium=webchat`}
                  target="_blank"
                  rel="noopener nofollow"
                >
                  <SVG name="missive" size={18} /> We run on Missive
                </a>
              </div>
            )}
          </div>
        </div>

        {this.state.canCompose && !this.state.error && (
          <div
            id="footer"
            ref={(footer) => (this.footer = footer)}
            onClick={(e) => {
              if (e.target != this.footer) return
              this.focusChat()
            }}
          >
            <div id="composer">
              <textarea
                id="shadow"
                ref={(shadowTextarea) => (this.shadowTextarea = shadowTextarea)}
              />
              <textarea
                tabIndex={state.opened ? 1 : -1}
                disabled={!this.state.internet}
                ref={(textarea) => (this.textarea = textarea)}
                onInput={() => this.handleInput()}
                onFocus={() => this.handleFocus()}
                onKeyDown={(e) => this.handleKeyDown(e)}
                placeholder={
                  this.state.internet ? messages.chat : messages.internet
                }
              />
            </div>

            {this.state.internet && (
              <div id="tools">
                {!isMobile && (
                  <div
                    class="tool"
                    key="smilies"
                    data-active={!!state.emoji}
                    onClick={() => this.toggleEmoji()}
                  >
                    {state.emoji && !state.emojisLoaded && (
                      <div class="icon-loading">
                        <SVG name="circle" />
                      </div>
                    )}

                    <SVG name="smilies" size={20} />
                  </div>
                )}

                {((isMobile && state.emptyTextarea) || !isMobile) && (
                  <div
                    class="tool"
                    key="attachment"
                    onmousedown={() => this.shadowFile.click()}
                  >
                    <SVG name="attachment" size={20} />
                    <input
                      type="file"
                      class="shadow"
                      ref={(shadowFile) => (this.shadowFile = shadowFile)}
                      onChange={(e) => this.handleFile(e)}
                    />
                  </div>
                )}

                {isMobile && !state.emptyTextarea && (
                  <div
                    class="tool"
                    key="send"
                    onmousedown={(e) => this.submitMessage(e)}
                  >
                    <SVG name="send" size={20} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!isMobile && state.emoji && (
          <div class="emoji">
            <Suspense>
              <Picker
                ref={() => {
                  if (!this.state.emojisLoaded) {
                    this.setState({ emojisLoaded: true })
                  }
                }}
                set="emojione"
                native={true}
                color="var(--main-color)"
                emoji="point_up"
                title="Pick your emoji…"
                onSelect={(emoji, e) => this.handleEmoji(emoji, e)}
              />
            </Suspense>
          </div>
        )}

        {state.dragging && (
          <div id="drop-zone">
            <input type="file" onChange={(e) => this.handleFile(e)} />
          </div>
        )}
      </div>
    )
  }
}

window.component = null

window.parent.postMessage({ event: 'missive-webchat-init' }, '*')
window.addEventListener('message', (e) => {
  if (e.source == window) return
  let { messageId, event, data } = e.data

  let promise = null
  if (
    messageId &&
    component &&
    component.promises &&
    (promise = component.promises[messageId])
  ) {
    promise.resolve(data)
    delete component.promises[messageId]
    return
  }

  switch (event) {
    case 'missive-webchat-init':
      if ('Rollbar' in window) {
        Rollbar.configure({
          payload: { person: { id: data.config.id } },
        })
      }

      render(
        <Webchat
          ref={(_component) => {
            component = _component
          }}
          {...data}
        />,
        document.querySelector('#component'),
      )
      break

    case 'missive-webchat-exec':
      const { method, params } = data
      component && component[method] && component[method](params)
      break
  }
})
